import i18next, { BackendModule, ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { i18n as zdsNavI18n } from '@zds3/zds-components';
import { ZuoraLanguages } from './types';
import en from './locales/en';

/**
 * All languages the nav supports. English is bundled directly (see below);
 * every other language lives in its own chunk and is fetched on demand. Add a
 * new language here AND have a matching `./locales/<code>.ts` provided (until
 * the file exists, requests for that language fail gracefully and fall back to
 * English). Region-qualified codes use the BCP-47 hyphen form (e.g. `zh-CN`).
 */
export const SUPPORTED_LANGUAGES: ZuoraLanguages[] = [
  'bg', // Bulgarian
  'bn', // Bengali
  'ca', // Catalan
  'cs', // Czech
  'da', // Danish
  'de', // German
  'el', // Greek
  'en', // English (bundled fallback)
  'es', // Spanish
  'et', // Estonian
  'fi', // Finnish
  'fil', // Filipino
  'fr', // French
  'hi', // Hindi
  'hr', // Croatian
  'hu', // Hungarian
  'id', // Indonesian
  'it', // Italian
  'ja', // Japanese
  'kk', // Kazakh
  'km', // Khmer
  'ko', // Korean
  'lt', // Lithuanian
  'lv', // Latvian
  'ms', // Malay
  'mt', // Maltese
  'my', // Burmese
  'nb', // Norwegian Bokmål
  'nl', // Dutch
  'pl', // Polish
  'pt', // Portuguese
  'ro', // Romanian
  'ru', // Russian
  'sk', // Slovak
  'sl', // Slovenian
  'sv', // Swedish
  'ta', // Tamil
  'th', // Thai
  'tr', // Turkish
  'uk', // Ukrainian
  'vi', // Vietnamese
  'zh-CN', // Simplified Chinese
  'zh-TW', // Traditional Chinese
];

/**
 * English is the fallback language, so it must be available synchronously on
 * first paint — we keep it in the main bundle. Every other locale is loaded
 * lazily so the bundle (and i18next's in-memory resources) only ever carry the
 * fallback plus whatever language the current user actually needs, regardless
 * of how many languages we support.
 */
const loadLazyLocale = (language: string): Promise<Record<string, unknown>> =>
  import(
    /* webpackChunkName: "nav-locale-[request]" */
    `./locales/${language}`
  ).then((module) => module.default);

const lazyLocaleBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: (language: string, _namespace: string, callback: ReadCallback) => {
    // English is already bundled via `resources` below; never fetch a chunk.
    if (language === 'en') {
      callback(null, en);
      return;
    }

    if (!SUPPORTED_LANGUAGES.includes(language as ZuoraLanguages)) {
      callback(new Error(`Unsupported nav locale: ${language}`), false);
      return;
    }

    loadLazyLocale(language)
      .then((resource) => callback(null, resource))
      .catch((error) => callback(error, false));
  },
};

i18next
  .use(lazyLocaleBackend)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    // debug: true,
    // Locale catalogs use flat dot-notation keys (e.g.
    // 'GlobalNav.Sections.ExtensionStudio'), so the dot must NOT be treated as a
    // nesting separator — `t('A.B.C')` looks up the literal key.
    keySeparator: false,
    nsSeparator: '|',
    // Lets i18next combine the statically bundled `resources` (English) with
    // languages resolved through the backend on demand.
    partialBundledLanguages: true,
    resources: {
      en: {
        translation: en,
      },
    },
  });

/**
 * Normalizes separator and BCP-47 casing for incoming language codes
 * (for example `zh_cn` -> `zh-CN`). This helper is intentionally narrow: it
 * does not map locale aliases or unsupported region variants onto one of our
 * shipped catalogs.
 */
export const normalizeLanguageCode = (langCode: string): string => {
  const [language, ...subtags] = langCode.replace(/_/g, '-').split('-');
  if (!language) {
    return langCode;
  }

  return [
    language.toLowerCase(),
    ...subtags.map((subtag) => {
      if (subtag.length === 2 || subtag.length === 3) {
        return subtag.toUpperCase();
      }

      if (subtag.length === 4) {
        return subtag.charAt(0).toUpperCase() + subtag.slice(1).toLowerCase();
      }

      return subtag;
    }),
  ].join('-');
};

interface I18nWithPendingLanguage {
  isInitialized: boolean;
  language: string;
  resolvedLanguage?: string;
  isLanguageChangingTo?: string;
  services?: {
    backendConnector?: {
      state?: Record<string, number>;
    };
  };
}

export const isI18nLanguageReady = ({
  i18n,
  targetLanguage,
}: {
  i18n: I18nWithPendingLanguage;
  targetLanguage: string;
}): boolean => {
  if (!i18n.isInitialized) {
    return false;
  }

  const normalizedTargetLanguage = normalizeLanguageCode(targetLanguage);
  const activeLanguage = normalizeLanguageCode(
    i18n.isLanguageChangingTo || i18n.language,
  );

  if (activeLanguage !== normalizedTargetLanguage) {
    return false;
  }

  const namespaceStates = i18n.services?.backendConnector?.state ?? {};
  const resolvedLanguage = normalizeLanguageCode(
    i18n.resolvedLanguage || i18n.language,
  );
  const targetNamespaceState =
    namespaceStates[`${normalizedTargetLanguage}|translation`];
  const resolvedNamespaceState =
    namespaceStates[`${resolvedLanguage}|translation`];

  // i18next marks namespace loads as:
  //   1  -> pending
  //   2  -> loaded
  //   -1 -> failed
  // We are ready once the requested locale is no longer pending AND the
  // catalog i18next actually resolved to is available. That covers direct
  // hits (ja→ja), region/base fallback (de-DE→de), and failed chunk fallback
  // (ja chunk 404s, resolvedLanguage falls back to en).
  const isTargetLanguageSettled =
    normalizedTargetLanguage === 'en' ||
    resolvedLanguage === normalizedTargetLanguage ||
    targetNamespaceState === -1 ||
    targetNamespaceState === 2;

  const isResolvedLanguageReady =
    resolvedLanguage === 'en' ||
    resolvedNamespaceState === -1 ||
    resolvedNamespaceState === 2;

  return isTargetLanguageSettled && isResolvedLanguageReady;
};

// Serializes language switches in call order. changeLanguage now awaits a lazy
// locale chunk, so concurrent calls can resolve out of order — a slower earlier
// request (e.g. a cached Revenue language) could otherwise apply after a faster
// later one (the Billing identity language) and leave i18next/zds on the wrong
// locale. Chaining each call onto the previous guarantees the last-requested
// language wins for both i18next and zds.
let languageSwitchQueue: Promise<void> = Promise.resolve();

/** Locale i18next actually renders with (may differ from `language` after fallback). */
const getEffectiveI18nextLocale = (): string =>
  i18next.resolvedLanguage || i18next.language;

const syncZdsToI18next = (): void => {
  const effectiveLocale = getEffectiveI18nextLocale();
  if (zdsNavI18n.getLocale() !== effectiveLocale) {
    zdsNavI18n.setLocale(effectiveLocale);
  }
};

const getNamespaceLoadState = (language: string): number | undefined => {
  const namespaceStates =
    (i18next as I18nWithPendingLanguage).services?.backendConnector?.state ??
    {};
  return namespaceStates[`${normalizeLanguageCode(language)}|translation`];
};

/** Whether changeLanguage should run (switch or retry after a failed chunk load). */
export const shouldLoadLanguage = (normalized: string): boolean => {
  if (!i18next.isInitialized) {
    return false;
  }

  const normalizedCurrent = normalizeLanguageCode(i18next.language);
  if (normalized !== normalizedCurrent) {
    return true;
  }

  // Same requested language — retry non-English locales whose catalog did not load.
  if (normalized === 'en') {
    return false;
  }

  const effectiveLocale = normalizeLanguageCode(getEffectiveI18nextLocale());
  if (effectiveLocale !== normalized) {
    return true;
  }

  return getNamespaceLoadState(normalized) === -1;
};

const applyLanguage = async (langCode: ZuoraLanguages): Promise<void> => {
  const normalized = normalizeLanguageCode(langCode);
  try {
    if (shouldLoadLanguage(normalized)) {
      // changeLanguage resolves only after the backend has loaded the locale
      // chunk, so awaiting guarantees the translations are ready before render.
      await i18next.changeLanguage(normalized);
    }
    syncZdsToI18next();
  } catch (error) {
    // If i18next.changeLanguage fails, i18next may have:
    // 1. Stayed on the previous language (if changeLanguage threw before changing state)
    // 2. Fallback to English (due to fallbackLng: 'en' config)
    // Either way, sync ZDS to whatever locale i18next actually resolved to.
    console.warn(`Failed to switch nav language to "${normalized}":`, error);
    syncZdsToI18next();
  }
};

export const i18nChangeLanguage = (langCode: ZuoraLanguages): Promise<void> => {
  languageSwitchQueue = languageSwitchQueue.then(() => applyLanguage(langCode));
  return languageSwitchQueue;
};

export default i18next;
